import type { EventBinding } from "../api/events";
import type { EventStore } from "./event-operation-store";
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const KEY = /^auction-template:[a-z0-9]+:[0-9a-f]{36}$/;
export interface TemplateOperation {
  operationType: "auction.template.save";
  templateId?: string;
  expectedVersion: string;
  idempotencyKey: string;
  startedAt: number;
}
export function templateStorageKey(binding: EventBinding) {
  if (
    binding.audience !== "workspace" ||
    !UUID.test(binding.tenantId) ||
    !UUID.test(binding.accountId)
  )
    throw new Error("Invalid template authority binding");
  return `wio-veyra.auction-template-operation.v1:${binding.tenantId}:${binding.accountId}`;
}
function project(value: unknown): TemplateOperation {
  const x = value as Partial<TemplateOperation> | null;
  if (
    !x ||
    x.operationType !== "auction.template.save" ||
    !/^(0|[1-9]\d{0,17})$/.test(x.expectedVersion ?? "") ||
    !KEY.test(x.idempotencyKey ?? "") ||
    typeof x.startedAt !== "number" ||
    !Number.isFinite(x.startedAt) ||
    x.startedAt < 0 ||
    (x.templateId !== undefined && !UUID.test(x.templateId)) ||
    (x.expectedVersion === "0") !== (x.templateId === undefined)
  )
    throw new Error("Invalid template recovery locator");
  return {
    operationType: x.operationType,
    expectedVersion: x.expectedVersion!,
    templateId: x.templateId,
    idempotencyKey: x.idempotencyKey!,
    startedAt: x.startedAt,
  };
}
export function readTemplateOperation(
  store: EventStore,
  binding: EventBinding,
) {
  const text = store.getItem(templateStorageKey(binding));
  return text === null ? undefined : project(JSON.parse(text));
}
export function persistTemplateOperation(
  store: EventStore,
  binding: EventBinding,
  value: TemplateOperation,
) {
  const before = readTemplateOperation(store, binding),
    row = project(value);
  if (before && before.idempotencyKey !== row.idempotencyKey)
    throw new Error("Unresolved template command");
  const key = templateStorageKey(binding),
    text = JSON.stringify(row);
  store.setItem(key, text);
  if (store.getItem(key) !== text)
    throw new Error("Template recovery unavailable");
}
export function removeTemplateOperation(
  store: EventStore,
  binding: EventBinding,
  commandKey: string,
) {
  const row = readTemplateOperation(store, binding);
  if (row && row.idempotencyKey !== commandKey)
    throw new Error("Mismatched template command");
  const key = templateStorageKey(binding);
  store.removeItem(key);
  if (store.getItem(key) !== null)
    throw new Error("Template recovery unavailable");
}
export function templateCompletion(
  value: unknown,
  operation: TemplateOperation,
) {
  if (!value || typeof value !== "object") return undefined;
  const x = value as Record<string, unknown>,
    field = (name: string) =>
      x[name] ?? x[name[0].toUpperCase() + name.slice(1)];
  if (
    !UUID.test(String(field("resourceId"))) ||
    !UUID.test(String(field("revisionId"))) ||
    (operation.templateId && operation.templateId !== field("resourceId")) ||
    String(field("version")) !==
      String(BigInt(operation.expectedVersion) + BigInt(1))
  )
    return undefined;
  return String(field("resourceId"));
}
